

import React, { useEffect, useState } from "react";
import { Table, Button, Tag, message, Space, Modal, Input } from "antd";
import dayjs from "dayjs";
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    updateDoc,
    runTransaction,
    increment,
    orderBy,
} from "firebase/firestore";
import { db } from "@/firebaseConfig.js";
import ProcessedTransactionsTable from "@components/AdminPanel/ProcessedTransactionsTable.jsx";

const { TextArea } = Input;

const AdminWithdrawalsTable = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [currentAction, setCurrentAction] = useState(null); // 'approve' или 'reject'
    const [currentTransactionId, setCurrentTransactionId] = useState(null);
    const [adminMessage, setAdminMessage] = useState("");

    useEffect(() => {
        const q = query(collection(db, "transactions"), where("status", "==", "pending"), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate(),
            }));
            setTransactions(data);
        });

        return () => unsubscribe();
    }, []);

    const openModal = (id, action) => {
        setCurrentTransactionId(id);
        setCurrentAction(action);
        setAdminMessage("");
        setModalVisible(true);
    };

    const handleOk = async () => {
        setLoading(true);

        const transactionRef = doc(db, "transactions", currentTransactionId);

        try {
            if (currentAction === "approve") {
                await runTransaction(db, async (t) => {
                    const txDoc = await t.get(transactionRef);
                    if (!txDoc.exists()) throw new Error("Transaction not found");
                    if (txDoc.data().status !== "pending") throw new Error("Transaction already processed");

                    const transaction = txDoc.data();
                    const userRef = doc(db, "users", transaction.userId);

                    t.update(transactionRef, { status: "approved", review: adminMessage || null });
                    t.update(userRef, { balance: increment(-transaction.amount) });
                });
                message.success("Transaction approved");
            } else if (currentAction === "reject") {
                await updateDoc(transactionRef, { status: "rejected", review: adminMessage || null });
                message.success("Transaction rejected");
            }
            setModalVisible(false);
        } catch (err) {
            message.error("Error: " + err.message);
        } finally {
            setLoading(false);
            setCurrentAction(null);
            setCurrentTransactionId(null);
            setAdminMessage("");
        }
    };

    const handleCancel = () => {
        setModalVisible(false);
        setAdminMessage("");
    };

    const columns = [
        {
            title: "User ID",
            dataIndex: "userEmail",
            key: "userEmail",
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            render: (amount) => `$${amount.toFixed(2)}`,
        },
        {
            title: "Method",
            dataIndex: "method",
            key: "method",
            render: (method) => <Tag color="purple">{method}</Tag>,
        },
        {
            title: "Admin Message",
            dataIndex: "review",
            key: "review",
            render: (review) => review ? <Tag color="orange">{review}</Tag> : "—",
        },
        {
            title: "Requested At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => (date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "—"),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space size="middle">
                    <Button onClick={() => openModal(record.id, "approve")} type="primary">
                        Approve
                    </Button>
                    <Button onClick={() => openModal(record.id, "reject")} danger>
                        Reject
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className={`wrapper`}>
            <div className={`page page-app`}>
                <h2>Pending Withdrawals</h2>
                <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={transactions}
                    columns={columns}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: "No pending transactions" }}
                />

                <Modal
                    title={currentAction === "approve" ? "Approve Transaction" : "Reject Transaction"}
                    open={modalVisible}
                    onOk={handleOk}
                    confirmLoading={loading}
                    onCancel={handleCancel}
                    okText={currentAction === "approve" ? "Approve" : "Reject"}
                >
                    <p>Please leave a message for the user (optional):</p>
                    <TextArea
                        rows={4}
                        value={adminMessage}
                        onChange={(e) => setAdminMessage(e.target.value)}
                        placeholder="Enter message to user"
                    />
                </Modal>

                <ProcessedTransactionsTable></ProcessedTransactionsTable>
            </div>
        </div>
    );
};

export default AdminWithdrawalsTable;


